import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getNewsletters, deleteNewsletter } from '../services/newsletters';
import PageLoading from '../components/PageLoading';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import Pagination from '../components/Pagination';
import { useConfirm } from '../hooks/useConfirm';
import { formatRelativeDate } from '../utils/dateUtils';
import { setTitle } from '../utils/environment';

const LIMIT = 20;

const STATUS_BADGE = {
  pending:    'badge-fun-secondary',
  generating: 'badge-fun-primary',
  completed:  'badge-fun-mint',
  failed:     'badge-fun-coral',
};

const formatWindow = (start, end) => {
  const fmt = (s) => new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
};

const Newsletters = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [newsletters, setNewsletters] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchNewsletters = useCallback(async (p = page) => {
    setIsLoading(true);
    try {
      const data = await getNewsletters({ limit: LIMIT, offset: (p - 1) * LIMIT });
      setNewsletters(data.newsletters || []);
      setTotal(data.total || 0);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load newsletters');
    } finally {
      setIsLoading(false);
    }
  }, [page]);

  useEffect(() => {
    setTitle('Newsletters · Read It Never');
    fetchNewsletters(page);
  }, [page, fetchNewsletters]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!(await confirm({
      title: 'Delete newsletter?',
      message: 'This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    setDeletingId(id);
    try {
      await deleteNewsletter(id);
      await fetchNewsletters(page);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  if (isLoading && newsletters.length === 0) {
    return <PageLoading message="Loading newsletters…" />;
  }

  return (
    <div className="min-h-screen bg-clay-bg">
      <PageHeader
        emoji="📰"
        title="Newsletters"
        subtitle={`${total} ${total === 1 ? 'newsletter' : 'newsletters'}`}
        backTo="/dashboard"
        backLabel="Back to dashboard"
        actions={<Link to="/newsletters/new" className="btn-clay text-sm">+ New newsletter</Link>}
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="notice-clay-error mb-4">{error}</div>
        )}

        {!isLoading && newsletters.length === 0 ? (
          <EmptyState
            icon="📰"
            title="No newsletters yet"
            description="Generate a newsletter to get started."
          />
        ) : (
          <div className="space-y-3">
            {newsletters.map((nl) => (
              <div
                key={nl.id}
                onClick={() => navigate(`/newsletters/${nl.id}`)}
                className="card-clay p-4 flex items-start justify-between gap-4 cursor-pointer hover:shadow-clay-lg transition-shadow"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-semibold text-heading truncate">{nl.theme_name}</span>
                    <span className={`${STATUS_BADGE[nl.status] || 'badge-fun-secondary'} text-xs`}>
                      {nl.status}
                    </span>
                    <span className="text-xs text-secondary-text uppercase tracking-wide">{nl.language}</span>
                  </div>
                  <div className="text-sm text-secondary-text">
                    {formatWindow(nl.window_start, nl.window_end)}
                    <span className="mx-2">·</span>
                    {formatRelativeDate(nl.created_at)}
                  </div>
                  {nl.status === 'failed' && nl.failure_message && (
                    <p className="mt-1 text-xs text-rose-600 dark:text-rose-400 line-clamp-2">
                      {nl.failure_message}
                    </p>
                  )}
                </div>

                {(nl.status === 'completed' || nl.status === 'failed') && (
                  <button
                    onClick={(e) => handleDelete(e, nl.id)}
                    disabled={deletingId === nl.id}
                    className="btn-clay-danger btn-clay-sm flex-shrink-0"
                  >
                    {deletingId === nl.id ? '…' : 'Delete'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={total}
              itemsPerPage={LIMIT}
              onPageChange={setPage}
              itemLabel="newsletters"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Newsletters;
